import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// A4 portrait, in the millimetre unit jsPDF is created with.
const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 10;

const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - MARGIN_MM * 2;
const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - MARGIN_MM * 2;

/**
 * Rasterise a DOM node and save it as an A4 PDF.
 *
 * The node is printed at its own aspect ratio, scaled to the page width, and
 * sliced across as many pages as it needs. Callers used to hand jsPDF a fixed
 * `addImage(..., 0, 0, 200, 100)` box, which squashed a full-page prescription
 * into a 200x100mm strip at the top of page one and dropped everything below it.
 *
 * The node may live off-screen (see `.prescription_download_container`);
 * html2canvas measures the cloned node, so negative offsets are fine. It must
 * still be laid out though — `display: none` or `visibility: hidden` renders blank.
 *
 * @param {HTMLElement} element node to print
 * @param {string} fileName saved file name, with or without the .pdf suffix
 */
export async function downloadElementAsPdf(element, fileName) {
  if (!element) {
    throw new Error("Nothing to print");
  }

  const canvas = await html2canvas(element, {
    // 2x keeps small print (the drug table) readable instead of soft.
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  if (!canvas.width || !canvas.height) {
    throw new Error("Nothing to print");
  }

  const imageData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  const imageHeightMm = (canvas.height * CONTENT_WIDTH_MM) / canvas.width;

  // Page 1 starts at the top margin; every later page shifts the same image up
  // by one page of content, so the overflow continues where the last page ended.
  let renderedMm = 0;
  let page = 0;
  while (renderedMm < imageHeightMm) {
    if (page > 0) pdf.addPage();
    pdf.addImage(
      imageData,
      "PNG",
      MARGIN_MM,
      MARGIN_MM - renderedMm,
      CONTENT_WIDTH_MM,
      imageHeightMm,
      undefined,
      "FAST",
    );
    renderedMm += CONTENT_HEIGHT_MM;
    page += 1;
  }

  pdf.save(fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`);
}
